import React, { useState, useEffect } from 'react';
import { Button, Table, Alert, Badge, Card, Modal } from 'react-bootstrap';
import { CacheManager } from '../../../core/cache-manager';
import { LanguageProvider } from '../../../core/language-provider';
import { settings } from '../../../config/settings';

const SettingsCache = () => {
    const [cacheData, setCacheData] = useState([]);
    const [cacheStats, setCacheStats] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [lastPullTime, setLastPullTime] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [cacheEnabled, setCacheEnabled] = useState(CacheManager.isCacheEnabled());

    // Load cache data on component mount
    useEffect(() => {
        loadCacheData();
    }, []);

    const loadCacheData = () => {
        try {
            // Get cache entries from CacheManager
            const entries = [];
            
            // Access the cache Map directly
            for (const [key, value] of CacheManager.cache.entries()) {
                entries.push({
                    key: key,
                    timestamp: new Date(value.timestamp),
                    expiry: new Date(value.expiry),
                    size: JSON.stringify(value.data).length,
                    type: key.split(':')[0], // getItems, getItem, searchItems
                    entity: key.split(':')[1],
                    isExpired: Date.now() > value.expiry,
                    data: value.data // Store actual data for detail view
                });
            }

            // Sort by timestamp (newest first)
            entries.sort((a, b) => b.timestamp - a.timestamp);
            
            setCacheData(entries);

            // Calculate stats
            const stats = {
                totalEntries: entries.length,
                totalSize: entries.reduce((sum, entry) => sum + entry.size, 0),
                expiredEntries: entries.filter(entry => entry.isExpired).length,
                entitiesCached: [...new Set(entries.map(entry => entry.entity))].length
            };
            setCacheStats(stats);

            // Get last pull time
            const pullTime = localStorage.getItem(CacheManager.LAST_PULL_KEY);
            if (pullTime) {
                setLastPullTime(new Date(parseInt(pullTime)));
            }

        } catch (error) {
            console.error('Error loading cache data:', error);
        }
    };

    const clearAllCache = () => {
        try {
            CacheManager.clearAll();
            loadCacheData();
            setSuccessMessage(LanguageProvider.get('cache.cache_cleared_success'));
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error clearing cache:', error);
        }
    };

    const clearExpiredEntries = () => {
        try {
            CacheManager.clearExpiredEntries();
            loadCacheData();
            setSuccessMessage(LanguageProvider.get('cache.expired_cleared_success'));
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error clearing expired entries:', error);
        }
    };

    const clearEntityCache = (entity) => {
        try {
            CacheManager.clearEntity(entity);
            loadCacheData();
            setSuccessMessage(LanguageProvider.get('cache.entity_cache_cleared_success').replace('{entity}', entity));
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error clearing entity cache:', error);
        }
    };

    const showEntryDetails = (entry) => {
        setSelectedEntry(entry);
        setShowDetailModal(true);
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setSelectedEntry(null);
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (date) => {
        return date.toLocaleString();
    };

    const getTypeVariant = (type) => {
        switch (type) {
            case 'getItems': return 'primary';
            case 'getItem': return 'success';
            case 'searchItems': return 'warning';
            default: return 'secondary';
        }
    };

    return (
        <div className="mt-4">
            {/* Cache Status Alert */}
            <Alert variant={cacheEnabled ? "info" : "warning"} className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <strong>{LanguageProvider.get('cache.cache_status')}</strong>
                        <Badge bg={cacheEnabled ? "success" : "danger"} className="ms-2">
                            {cacheEnabled ? LanguageProvider.get('cache.enabled') : LanguageProvider.get('cache.disabled')}
                        </Badge>
                        <div className="mt-1">
                            <small>
                                {cacheEnabled
                                    ? LanguageProvider.get('cache.cache_active')
                                    : LanguageProvider.get('cache.cache_disabled')
                                }
                            </small>
                        </div>
                    </div>
                    {!cacheEnabled && (
                        <div className="text-end">
                            <small className="text-muted">
                                {LanguageProvider.get('cache.enable_cache')} <code>usingCache: true</code> {LanguageProvider.get('cache.in_settings')}
                            </small>
                        </div>
                    )}
                </div>
            </Alert>

            {successMessage && (
                <Alert variant="success" className="mb-3">
                    {successMessage}
                </Alert>
            )}

            {/* Cache Statistics */}
            <Card className="mb-4">
                <Card.Header>
                    <h5>{LanguageProvider.get('cache.cache_statistics')} {!cacheEnabled && <Badge bg="secondary">{LanguageProvider.get('cache.disabled')}</Badge>}</h5>
                </Card.Header>
                <Card.Body>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="text-center">
                                <h4 className="text-primary">{cacheStats.totalEntries}</h4>
                                <small className="text-muted">{LanguageProvider.get('cache.total_entries')}</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-center">
                                <h4 className="text-success">{cacheStats.entitiesCached}</h4>
                                <small className="text-muted">{LanguageProvider.get('cache.entities_cached')}</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-center">
                                <h4 className="text-info">{formatBytes(cacheStats.totalSize)}</h4>
                                <small className="text-muted">{LanguageProvider.get('cache.total_size')}</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-center">
                                <h4 className={cacheStats.expiredEntries > 0 ? "text-warning" : "text-secondary"}>
                                    {cacheStats.expiredEntries}
                                </h4>
                                <small className="text-muted">{LanguageProvider.get('cache.expired_entries')}</small>
                            </div>
                        </div>
                    </div>

                    {lastPullTime && (
                        <div className="mt-3 text-center">
                            <small className="text-muted">
                                {LanguageProvider.get('cache.last_sync_check')} {formatDate(lastPullTime)}
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="mb-4 text-center">
                <Button
                    variant="danger"
                    onClick={clearAllCache}
                    className="me-2 mb-2"
                    disabled={!cacheEnabled}
                >
                    {LanguageProvider.get('cache.clear_all_cache')}
                </Button>
                <Button
                    variant="warning"
                    onClick={clearExpiredEntries}
                    className="me-2 mb-2"
                    disabled={!cacheEnabled || cacheStats.expiredEntries === 0}
                >
                    {LanguageProvider.get('cache.clear_expired')} ({cacheStats.expiredEntries})
                </Button>
                <Button
                    variant="info"
                    onClick={loadCacheData}
                    className="mb-2"
                >
                    {LanguageProvider.get('cache.refresh')}
                </Button>
            </div>

            {/* Cache Entries Table */}
            {cacheData.length > 0 ? (
                <Card>
                    <Card.Header>
                        <h5>{LanguageProvider.get('cache.cache_entries')} ({cacheData.length})</h5>
                    </Card.Header>
                    <Card.Body>
                        <div className="table-responsive">
                            <Table striped bordered hover size="sm" style={{fontSize: '0.8rem'}}>
                                <thead>
                                    <tr>
                                        <th>{LanguageProvider.get('cache.type')}</th>
                                        <th>{LanguageProvider.get('cache.entity')}</th>
                                        <th>{LanguageProvider.get('cache.cached_at')}</th>
                                        <th>{LanguageProvider.get('cache.expires_at')}</th>
                                        <th>{LanguageProvider.get('cache.size')}</th>
                                        <th>{LanguageProvider.get('cache.status')}</th>
                                        <th>{LanguageProvider.get('cache.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cacheData.map((entry, index) => (
                                        <tr key={index} className={entry.isExpired ? 'table-warning' : ''}>
                                            <td>
                                                <Badge bg={getTypeVariant(entry.type)} style={{fontSize: '0.7rem'}}>
                                                    {entry.type}
                                                </Badge>
                                            </td>
                                            <td>
                                                <code style={{fontSize: '0.75rem'}}>{entry.entity}</code>
                                            </td>
                                            <td style={{fontSize: '0.75rem'}}>{formatDate(entry.timestamp)}</td>
                                            <td style={{fontSize: '0.75rem'}}>{formatDate(entry.expiry)}</td>
                                            <td style={{fontSize: '0.75rem'}}>{formatBytes(entry.size)}</td>
                                            <td>
                                                {entry.isExpired ? (
                                                    <Badge bg="warning" style={{fontSize: '0.7rem'}}>{LanguageProvider.get('cache.expired')}</Badge>
                                                ) : (
                                                    <Badge bg="success" style={{fontSize: '0.7rem'}}>{LanguageProvider.get('cache.valid')}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-info"
                                                        onClick={() => showEntryDetails(entry)}
                                                        style={{fontSize: '0.7rem', padding: '0.2rem 0.4rem'}}
                                                    >
                                                        {LanguageProvider.get('cache.detail')}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => clearEntityCache(entry.entity)}
                                                        style={{fontSize: '0.7rem', padding: '0.2rem 0.4rem'}}
                                                    >
                                                        {LanguageProvider.get('cache.clear')}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            ) : (
                <Card>
                    <Card.Body className="text-center py-5">
                        <h5 className="text-muted">{LanguageProvider.get('cache.no_cache_entries')}</h5>
                        <p className="text-muted">
                            {LanguageProvider.get('cache.no_cache_entries_desc')}
                        </p>
                    </Card.Body>
                </Card>
            )}

            {/* Detail Modal */}
            <Modal show={showDetailModal} onHide={closeDetailModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{LanguageProvider.get('cache.cache_entry_details')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedEntry && (
                        <div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>{LanguageProvider.get('cache.type')}:</strong>
                                    <Badge bg={getTypeVariant(selectedEntry.type)} className="ms-2">
                                        {selectedEntry.type}
                                    </Badge>
                                </div>
                                <div className="col-md-6">
                                    <strong>{LanguageProvider.get('cache.entity')}:</strong>
                                    <code className="ms-2">{selectedEntry.entity}</code>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>{LanguageProvider.get('cache.cached_at')}:</strong> {formatDate(selectedEntry.timestamp)}
                                </div>
                                <div className="col-md-6">
                                    <strong>{LanguageProvider.get('cache.expires_at')}:</strong> {formatDate(selectedEntry.expiry)}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>{LanguageProvider.get('cache.size')}:</strong> {formatBytes(selectedEntry.size)}
                                </div>
                                <div className="col-md-6">
                                    <strong>{LanguageProvider.get('cache.status')}:</strong>
                                    {selectedEntry.isExpired ? (
                                        <Badge bg="warning" className="ms-2">{LanguageProvider.get('cache.expired')}</Badge>
                                    ) : (
                                        <Badge bg="success" className="ms-2">{LanguageProvider.get('cache.valid')}</Badge>
                                    )}
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>{LanguageProvider.get('cache.cache_key')}:</strong>
                                <div className="mt-1">
                                    <code style={{fontSize: '0.85rem', wordBreak: 'break-all'}}>
                                        {selectedEntry.key}
                                    </code>
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>{LanguageProvider.get('cache.cached_data')}:</strong>
                                <div className="mt-2" style={{maxHeight: '400px', overflow: 'auto'}}>
                                    <pre style={{
                                        backgroundColor: 'rgba(100,100,100,0.2)',
                                        padding: '1rem',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.8rem',
                                        lineHeight: '1.4'
                                    }}>
                                        {JSON.stringify(selectedEntry.data, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDetailModal}>
                        {LanguageProvider.get('cache.close')}
                    </Button>
                    {selectedEntry && (
                        <Button
                            variant="danger"
                            onClick={() => {
                                clearEntityCache(selectedEntry.entity);
                                closeDetailModal();
                            }}
                        >
                            {LanguageProvider.get('cache.clear_entity_cache')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SettingsCache;